// Fonte única dos linknames de sonda no front.
//
// Antes esta lista estava repetida literalmente em ~6 pontos do NewAsideDevice.vue
// (e em outros componentes). Cada tipo novo de sonda exigia caçar todas as
// ocorrências, e esquecer uma causava falha silenciosa (o card some, o gráfico
// não carrega). Importar daqui evita isso.
export const SONDA_LINKNAMES = [
  "sonda-hidrostatica",
  "sonda-laser",
  "sonda-nivel",
  "sonda-caixa",
];

export function isSonda(linkname) {
  return SONDA_LINKNAMES.includes(linkname);
}

// Formatos possíveis de uma caixa d'água.
export const CAIXA_REDONDA = "REDONDA";
export const CAIXA_QUADRADA = "QUADRADA";

// Infere o formato da caixa a partir das dimensões que vêm na leitura.
// Diâmetro preenchido → redonda; lado1 e lado2 preenchidos → quadrada.
// Se ambos vierem, o diâmetro vence — regra determinística para o card não
// oscilar entre leituras. Espelha ReportDataResult.CaixaFormato no backend.
export function caixaFormato(leitura) {
  if (!leitura) return null;
  if (leitura.diametro != null && leitura.diametro !== 0) return CAIXA_REDONDA;
  if (leitura.lado1 != null && leitura.lado1 !== 0 && leitura.lado2 != null && leitura.lado2 !== 0) {
    return CAIXA_QUADRADA;
  }
  return null;
}

// Formato usado APENAS para desenhar o reservatório no card.
// Quando o firmware não manda a geometria, caixaFormato() retorna null e o card
// ficaria sem desenho. Aqui assumimos redonda como forma neutra só para ilustrar
// o nível — é seguro porque o desenho não é uma medição. Note que os cálculos
// (volume) continuam usando caixaFormato() e seguem retornando null: chutar
// dimensão produziria um volume em m³ com cara de dado real, o que não é aceitável.
export function caixaFormatoDesenho(leitura) {
  return caixaFormato(leitura) || CAIXA_REDONDA;
}

// True quando o desenho está usando a forma default por falta de geometria
// na leitura — o card usa isso para avisar o usuário.
export function caixaSemGeometria(leitura) {
  return caixaFormato(leitura) === null;
}

// Converte para número utilizável ou null. Rejeita null/undefined, NaN e
// Infinity — leituras com falha chegam assim e, sem isso, "NaN" e "Infinity"
// vazariam para a tela (toFixed(2) os formata sem erro).
function numeroFinito(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// --- Cálculos da caixa d'água ---------------------------------------------
// Todos derivam do que já vem na leitura (em metros, convertidos pelo backend):
//   nivel  = depthCalc (depth + offset)
//   altura = altura total da caixa
//   geometria = diametro (redonda) ou lado1 x lado2 (quadrada)

// Quanto da caixa está cheia, em %. Limitado a 0..100 porque o sensor pode
// reportar um nível acima da altura cadastrada (onda, calibração, transbordo).
export function caixaCapacidade(leitura) {
  if (!leitura) return null;
  const nivel = numeroFinito(leitura.depthCalc);
  const altura = numeroFinito(leitura.altura);
  // Altura <= 0 é cadastro inválido: não dá para calcular proporção.
  if (nivel === null || altura === null || altura <= 0) return null;
  return Math.max(0, Math.min(100, (nivel / altura) * 100));
}

// Volume de água em m³ = área da base × nível.
// Redonda: PI * r²  |  Quadrada: lado1 * lado2
export function caixaVolume(leitura) {
  if (!leitura) return null;
  const nivelBruto = numeroFinito(leitura.depthCalc);
  if (nivelBruto === null) return null;

  // Nível negativo (sensor descalibrado / fora d'água) não vira volume negativo.
  // Nível acima da altura satura no volume da caixa cheia — a caixa não guarda
  // mais água do que cabe, então reportar mais seria mentira.
  const altura = numeroFinito(leitura.altura);
  let nivel = Math.max(0, nivelBruto);
  if (altura !== null && altura > 0) nivel = Math.min(nivel, altura);

  const formato = caixaFormato(leitura);
  if (formato === CAIXA_REDONDA) {
    const d = numeroFinito(leitura.diametro);
    if (d === null || d <= 0) return null;
    const r = d / 2;
    return Math.PI * r * r * nivel;
  }
  if (formato === CAIXA_QUADRADA) {
    const l1 = numeroFinito(leitura.lado1);
    const l2 = numeroFinito(leitura.lado2);
    if (l1 === null || l2 === null || l1 <= 0 || l2 <= 0) return null;
    return l1 * l2 * nivel;
  }
  return null; // sem geometria não dá para calcular volume
}
